import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Star, CheckCircle2, MessageSquare, ArrowRight, Heart, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import GlobalFooter from '../components/GlobalFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_OPTIONS = [
  'Assessment Results',
  'Supplement Recommendations',
  'Wellness Journal',
  'Daily Check-ins & Streaks',
  'Condition Guides',
  'Ask AI Chat',
  'My Stack Tracker',
  'Weekly Reports',
];

export default function FeedbackPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [helpfulFeatures, setHelpfulFeatures] = useState([]);
  const [improvement, setImprovement] = useState('');
  const [nps, setNps] = useState(-1);
  const [testimonial, setTestimonial] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user) {
      axios.get(`${API}/feedback/status`, { headers: getAuthHeader() })
        .then(res => {
          if (res.data.has_submitted) setAlreadySubmitted(true);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user, getAuthHeader]);

  const toggleFeature = (f) => {
    setHelpfulFeatures(prev =>
      prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]
    );
  };

  const handleSubmit = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    if (nps === -1) { toast.error('Please select a recommendation score'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        rating,
        helpful_features: helpfulFeatures,
        improvement_suggestion: improvement,
        nps_score: nps,
        testimonial,
      }, { headers: getAuthHeader() });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl border border-border p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Thank You!</h1>
          <p className="text-muted-foreground mb-6">
            Your feedback means the world to us. It directly shapes what we build next.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="btn-primary rounded-full px-8">
            Back to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-border p-8 max-w-md w-full text-center">
          <MessageSquare className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Share Your Feedback</h1>
          <p className="text-muted-foreground mb-6">Please sign in to submit your feedback.</p>
          <Button onClick={() => navigate('/auth?mode=login&redirect=/feedback')} className="btn-primary rounded-full px-8">
            Sign In <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Already submitted
  if (alreadySubmitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-border p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Already Submitted</h1>
          <p className="text-muted-foreground mb-6">You've already shared your feedback. Thank you!</p>
          <Button onClick={() => navigate('/dashboard')} className="btn-primary rounded-full px-8">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border/50 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Logo size="small" linkTo="/" />
          <span className="text-xs text-muted-foreground">Takes ~2 minutes</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Intro */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2">How's Vitala Working for You?</h1>
            <p className="text-muted-foreground">Your honest feedback shapes what we build next.</p>
          </div>

          <div className="space-y-8">
            {/* Q1: Rating */}
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="feedback-rating">
              <h2 className="font-semibold text-[#1A1A1A] mb-1">How would you rate Vitala so far?</h2>
              <p className="text-xs text-muted-foreground mb-4">1 = Poor, 5 = Excellent</p>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`rating-star-${n}`}
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        n <= (hoverRating || rating)
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-gray-200'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-amber-600 mt-2 font-medium">
                  {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                </p>
              )}
            </div>

            {/* Q2: Helpful Features */}
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="feedback-features">
              <h2 className="font-semibold text-[#1A1A1A] mb-1">What's been most helpful?</h2>
              <p className="text-xs text-muted-foreground mb-4">Select all that apply</p>
              <div className="grid grid-cols-2 gap-2">
                {FEATURE_OPTIONS.map(f => (
                  <button
                    key={f}
                    onClick={() => toggleFeature(f)}
                    className={`text-left px-3 py-2.5 rounded-lg text-sm border transition-all ${
                      helpfulFeatures.includes(f)
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-border hover:border-primary/30 text-muted-foreground'
                    }`}
                    data-testid={`feature-${f.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {helpfulFeatures.includes(f) && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5" />}
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Q3: Improvement */}
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="feedback-improvement">
              <h2 className="font-semibold text-[#1A1A1A] mb-1">What could we improve?</h2>
              <p className="text-xs text-muted-foreground mb-4">Optional — but we read every response</p>
              <textarea
                value={improvement}
                onChange={(e) => setImprovement(e.target.value)}
                placeholder="Tell us what would make Vitala better for you..."
                className="w-full p-3 rounded-lg border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                rows={3}
                data-testid="improvement-input"
              />
            </div>

            {/* Q4: NPS */}
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="feedback-nps">
              <h2 className="font-semibold text-[#1A1A1A] mb-1">Would you recommend Vitala to a friend?</h2>
              <p className="text-xs text-muted-foreground mb-4">0 = Not at all, 10 = Absolutely</p>
              <div className="flex gap-1 justify-center flex-wrap">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <button
                    key={n}
                    onClick={() => setNps(n)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                      nps === n
                        ? n >= 9 ? 'bg-emerald-500 text-white' : n >= 7 ? 'bg-amber-400 text-white' : 'bg-red-400 text-white'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                    data-testid={`nps-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {nps >= 0 && (
                <p className={`text-center text-xs mt-2 font-medium ${
                  nps >= 9 ? 'text-emerald-600' : nps >= 7 ? 'text-amber-600' : 'text-red-500'
                }`}>
                  {nps >= 9 ? 'Promoter — Thank you!' : nps >= 7 ? 'Passive' : 'Detractor — We want to do better'}
                </p>
              )}
            </div>

            {/* Q5: Testimonial */}
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="feedback-testimonial">
              <h2 className="font-semibold text-[#1A1A1A] mb-1">Care to share a testimonial?</h2>
              <p className="text-xs text-muted-foreground mb-4">Optional — your words help others discover Vitala</p>
              <textarea
                value={testimonial}
                onChange={(e) => setTestimonial(e.target.value)}
                placeholder="What would you tell a friend about Vitala?"
                className="w-full p-3 rounded-lg border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                rows={3}
                data-testid="testimonial-input"
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || rating === 0 || nps === -1}
              className="w-full btn-primary rounded-xl py-6 text-base"
              data-testid="submit-feedback-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>Submit Feedback <ArrowRight className="w-5 h-5 ml-2" /></>
              )}
            </Button>
          </div>
        </motion.div>
      </div>

      <GlobalFooter />
    </div>
  );
}
